import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Form, Button, message, Icon } from "antd";
import { OrderedMap } from "immutable";
import {
    fieldGenerator,
    getErrorOnField
} from "utilities/form";
import {
    INPUT_TYPES
} from "constants/input-types";
import { GSTIN_REGEX } from "constants/regex";
import CompanyService from "services/company-service";

const getFormFields = (gstinCustomValidator) => new OrderedMap({
    gstin: {
        className: "form-field fb-50",
        type: INPUT_TYPES.TEXT,
        label: "GSTIN",
        placeholder: "29ABCDE1234F2Z2",
        rules: [
            gstinCustomValidator,
            {
                required: true,
                message: "GSTIN is Required"
            },
        ]
    },
    company_name: {
        className: "form-field fb-50",
        type: INPUT_TYPES.TEXT,
        label: "Name",
        disabled: true
    },
    program_anchor: {
        className: "form-field fb-50",
        label: "Program Anchor",
        type: INPUT_TYPES.OBJECT_SELECT,
        labelProp: "name",
        showSearch: true,
        getOptionsOnFocus: () => CompanyService.getCompanyProfiles(null, null, JSON.stringify([1,2])),
        onSearch: str => CompanyService.getCompanyProfiles(null, str, JSON.stringify([1,2])),
        rules: [
            {
                required: true,
                message: "Field is Required"
            }
        ]
    }
});

const USER_FIELDS = new OrderedMap({
    first_name: {
        className: "form-field fb-50",
        label: "First Name",
        type: INPUT_TYPES.TEXT,
        rules: [
            {
                required: true,
                message: "First Name is Required"
            },
            {
                //eslint-disable-next-line
                pattern: /^[A-Za-z]+$/,
                message: "Enter valid Name"
            }
        ]
    },
    last_name: {
        className: "form-field fb-50",
        label: "Last Name",
        type: INPUT_TYPES.TEXT,
        rules: [
            {
                required: true,
                message: "Last Name is Required"
            },
            {
                //eslint-disable-next-line
                pattern: /^[A-Za-z]+$/,
                message: "Enter valid Name"
            }
        ]
    },
    phone: {
        className: "form-field fb-50",
        label: "Mobile Number",
        type: INPUT_TYPES.TEXT,
        rules: [
            {
                required: true,
                message: "Mobile Number is Required"
            },
            {
                min: 10,
                message: "Enter valid mobile number "
            },
            {
                max: 10,
                message: "Enter valid mobile number "
            },
            {
                pattern: /^(?:\d*)$/,
                message: "Value should contain just number",
            },
        ]
    },
    email: {
        className: "form-field fb-50",
        label: "Email",
        type: INPUT_TYPES.TEXT,
        rules: [
            {
                required: true,
                message: "E-mail is Required"
            },
            {
                pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: "Enter valid Email"
            }
        ]
    }
});

class CreateDistributorForm extends React.Component {
    state = {
        loader: false,
        gstinValidated: false,
        gstin: null
    }
    componentWillUnmount() {
        this.setState({
            gstin: null,
            gstinValidated: false
        });
    }
    fieldGenerator = fieldGenerator.bind(this.props.form);
    getErrorOnField = getErrorOnField.bind(this.props.form);
    gstinCustomValidator = {
        validator: (rule, value, callback) => {
            if(!value){
                return callback();
            }
            if (value.length === 15 && !GSTIN_REGEX.test(value) || value.length !== 15 && value.length>0) {
                return callback("Invalid GSTIN");
            }
            const company_name = this.props.form.getFieldValue("company_name");
            if((!value || value.length < 15) && company_name) {
                this.props.form.setFieldsValue({
                    company_name: null
                });
            }
            if (value && value.length > 0 && GSTIN_REGEX.test(value) && !this.state.gstinValidated) {
                if(value !== (this.state.gstin && this.state.gstin.gstin) || !this.state.gstin) {
                    return CompanyService.parseGSTIN(value).then(({ data }) => {
                        if (!data || Object.keys(data).length === 0) {
                            callback("Not Found");
                            return;
                        }
                        this.setState({
                            gstin: data,
                            gstinValidated: false
                        }, () => {
                            if(!this.props.form.getFieldValue("company_name")){
                                this.props.form.setFieldsValue({
                                    company_name: this.state.gstin.tradeNam
                                });
                                this.setState({
                                    gstinValidated: true
                                });
                            }
                        });
                        return callback();
                    });
                }
            }
            return callback();
        }
    };

    formFields = getFormFields(this.gstinCustomValidator);

    onSubmit = () => {
        this.setState({
            loader: true
        });
        this.props.form.validateFields((err, values) => {
            if(values){
                this.setState({
                    loader:false
                });
            }
            if(!err) {
                CompanyService.createCompanyLosProfile({ ...values, type: 2, program_anchor: (values.program_anchor && values.program_anchor.id) }).then(() => {
                    this.setState({
                        loader: false
                    });
                    message.success("Distributor Created");
                    this.props.closeDistributorModal();
                }).catch(() => {
                    this.setState({
                        loader: false
                    });
                    message.error("Failed to Create Distributor");
                });
            }
        });
    }

    render() {
        return (
            <Form className={cx("form", "br-3")}>
                <div className={"form-row"}>
                    {this.formFields
                        .map((field, fieldName) => {
                            if(fieldName === "gstin" && this.state.gstinValidated) {
                                field.onChange = () => {
                                    this.setState({
                                        gstinValidated: false,
                                        gstin: null
                                    });
                                };
                            }
                            return this.fieldGenerator(
                                fieldName,
                                field,
                                this.getErrorOnField(fieldName)
                            )();
                        })
                        .toList()}
                </div>
                <h3 className={"pt-5 pb-5"}>{"Key Contact Details"}</h3>
                <div className={"form-row"}>
                    {USER_FIELDS
                        .map((field, fieldName) =>
                            this.fieldGenerator(
                                fieldName,
                                field,
                                this.getErrorOnField(fieldName)
                            )()
                        )
                        .toList()}
                </div>
                <div className="d-flex justify-content-flex-end">
                    <Form.Item className={cx("m-0")}>
                        <Button
                            type="primary"
                            onClick={this.onSubmit}
                        >
                            Create
                            {this.state.loader ? <Icon type={"loading"} /> : null}
                        </Button>
                    </Form.Item>
                </div>
            </Form>
        );
    }
}

CreateDistributorForm.propTypes = {
    form: PropTypes.object,
    closeDistributorModal: PropTypes.func
};

export default Form.create({})(CreateDistributorForm);